import React from 'react';
import { motion } from 'framer-motion';
import { ExternalLink } from 'lucide-react';
import { AITool } from '../types';

interface RelatedToolsProps {
  currentTool: AITool;
  allTools: AITool[];
  maxTools?: number;
}

export const RelatedTools: React.FC<RelatedToolsProps> = ({ 
  currentTool, 
  allTools,
  maxTools = 3
}) => {
  // Find related tools based on categories and use cases
  const relatedTools = React.useMemo(() => {
    // Filter out the current tool
    const otherTools = allTools.filter(tool => tool.id !== currentTool.id);
    
    // Calculate a relevance score for each tool
    const toolsWithRelevance = otherTools.map(tool => {
      let relevanceScore = 0;
      
      // Add points for matching categories
      const sharedCategories = tool.categories.filter(cat => 
        currentTool.categories.includes(cat)
      );
      relevanceScore += sharedCategories.length * 3;
      
      // Add points for matching use cases
      const sharedUseCases = tool.useCases.filter(useCase => 
        currentTool.useCases.includes(useCase)
      );
      relevanceScore += sharedUseCases.length * 2;
      
      // Add points for matching experience levels
      const sharedExperienceLevels = tool.experienceLevels.filter(level => 
        currentTool.experienceLevels.includes(level)
      );
      relevanceScore += sharedExperienceLevels.length;
      
      return { tool, relevanceScore };
    });
    
    // Sort by relevance score (highest first) and take the top N
    return toolsWithRelevance
      .sort((a, b) => b.relevanceScore - a.relevanceScore)
      .slice(0, maxTools)
      .map(item => item.tool);
  }, [currentTool, allTools, maxTools]);
  
  if (relatedTools.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-medium mb-4 text-gray-900 dark:text-gray-100">Related AI Tools</h3>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {relatedTools.map((tool, index) => (
          <motion.div
            key={tool.id}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
            className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center mb-2">
              <div className="w-8 h-8 rounded-full overflow-hidden mr-2 bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <img 
                  src={tool.logo} 
                  alt={`${tool.name} logo`} 
                  className="w-full h-full object-cover"
                  loading="lazy"
                  onError={(e) => {
                    (e.target as HTMLImageElement).src = 'https://images.unsplash.com/photo-1679083216051-aa510a1a2c0e?q=80&w=100&auto=format&fit=crop';
                  }}
                />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-gray-100">{tool.name}</h4>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-2">{tool.description}</p>
            <a
              href={tool.tryNowLink}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-sm text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300 font-medium"
            >
              Learn More <ExternalLink size={14} className="ml-1" />
            </a>
          </motion.div>
        ))}
      </div>
    </div>
  );
};